/*
 * /board-game-bank — the dialog: a payment, a request, a player's card,
 * a bankruptcy, a yes/no.
 *
 * One <dialog> (#bgbDlg) owned by the blade; this module fills its three rows
 * (head · body · foot). The body scrolls, the buttons stay the last row of the
 * column — never a sticky footer, never a page scroll.
 *
 * The summary of a payment is drawn as «who → whom · amount» with chips, not
 * as a sentence: «Анна платит Борису» needs the dative of a name nobody can
 * decline for us, in ten languages. An arrow reads the same in all of them.
 */
import * as L from './ledger.js';
import { h, t, $, clear, put, money, num, keypad, openDialog, closeDialog, list, sound } from './ui.js';

const { BANK, POT } = L;
let onKey = null;
let resolveConfirm = null;

function dlg() { return $('bgbDlg'); }

function show(head, body, foot, cls) {
    const d = dlg();
    d.className = 'bgb-dlg' + (cls ? ' ' + cls : '');
    put(clear($('bgbDlgHead')), ...[].concat(head));
    put(clear($('bgbDlgBody')), ...[].concat(body));
    put(clear($('bgbDlgFoot')), ...[].concat(foot));
    openDialog(d);
    // focus the dialog, not a field: on a phone a focused field opens the
    // system keyboard over the keypad the person came for
    try { d.focus({ preventScroll: true }); } catch (e) { /* old engines */ }
}

export function close() {
    onKey = null;
    closeDialog(dlg());
    if (resolveConfirm) { const r = resolveConfirm; resolveConfirm = null; r(false); }
}

export function init() {
    const d = dlg();
    if (!d || d.__bgb) return;
    d.__bgb = true;
    d.addEventListener('keydown', (e) => {
        if (onKey && onKey(e)) e.preventDefault();
    });
    d.addEventListener('close', () => {
        onKey = null;
        if (resolveConfirm) { const r = resolveConfirm; resolveConfirm = null; r(false); }
    });
    // a tap on the backdrop closes, a tap inside does not
    d.addEventListener('click', (e) => { if (e.target === d) close(); });
}

function closeBtn() {
    return h('button', { type: 'button', class: 'bgb-dlg-x', 'aria-label': t('ui.close'), text: '×', on: { click: close } });
}

export function acctLabel(game, id) {
    if (id === BANK) return '🏦 ' + t('ui.bank');
    if (id === POT) return '🅿️ ' + t('ui.pot');
    const p = L.player(game, id);
    return p ? p.token + ' ' + p.name : '?';
}

function acctChip(game, id, extra) {
    const p = L.player(game, id);
    return h('span', { class: 'bgb-acct' + (p ? '' : ' bgb-acct-bank'), style: p ? { '--pc': p.color } : null, text: acctLabel(game, id) + (extra || '') });
}

export function noteText(n) {
    if (!n) return '';
    if (n === 'go') return t('note.go');
    return n;
}

/* ── the money sheet ──────────────────────────────────────────────────────── */

/**
 * ctx: {pid, dir:'pay'|'get', guest:bool, cp?}
 * The banker opens it from a player's tile (both directions, every account);
 * a phone opens it from its own wallet: «pay» moves its own money, «get»
 * becomes a REQUEST the other side confirms.
 */
export function openMoney(app, ctx) {
    const g = app.game;
    const me = L.player(g, ctx.pid);
    if (!me) return;
    const guest = !!ctx.guest;
    const st = { dir: ctx.dir || 'pay', cp: ctx.cp || BANK, note: '', amount: null, all: false };
    const preset = L.PRESETS[g.preset] || L.PRESETS.classic;
    const rp = L.replay(g);
    const others = g.players.filter((p) => p.id !== me.id && rp.joined[p.id] && !rp.out[p.id]);

    const head = h('div', { class: 'bgb-dlg-hd' },
        h('span', { class: 'bgb-dlg-tok', style: { '--pc': me.color }, text: me.token, 'aria-hidden': 'true' }),
        h('div', { class: 'bgb-dlg-ti' },
            h('div', { class: 'bgb-dlg-t', id: 'bgbDlgTitle', text: me.name }),
            h('div', { class: 'bgb-dlg-s', text: t('ui.balance') + ': ' + money(rp.bal[me.id] || 0, g.cur) })),
        closeBtn());

    const seg = h('div', { class: 'bgb-seg', role: 'radiogroup', 'aria-label': t('ui.direction'), hidden: guest });
    const cps = h('div', { class: 'bgb-cps', role: 'radiogroup', 'aria-label': t('ui.counterparty') });
    const cpLabel = h('div', { class: 'bgb-dlg-l' });
    const sum = h('div', { class: 'bgb-sum', 'aria-live': 'polite' });
    const err = h('div', { class: 'bgb-err', role: 'alert', hidden: true });
    const ok = h('button', { type: 'button', class: 'lg-btn bgb-ok', id: 'bgbOk' });

    let kp = null;
    const padWrap = h('div', { class: 'bgb-padwrap' });
    function paintPad() {
        const allPot = st.cp === POT && st.dir === 'get';
        const ex = [];
        if (allPot && rp.pot > 0) ex.push({ label: t('ui.pot_all', { x: money(rp.pot, g.cur) }), value: rp.pot });
        if (!guest && st.dir === 'get' && st.cp === BANK && g.salary > 0) ex.push({ label: t('ui.salary_chip', { x: money(g.salary, g.cur) }), value: g.salary });
        const prev = kp ? kp.value() : null;
        kp = keypad({ value: prev, quick: preset.quick.length ? preset.quick : [10, 50, 100, 500], cur: g.cur, extra: ex, onChange: (v) => { st.amount = v; paintSum(); } });
        put(clear(padWrap), kp.el);
    }
    const tags = h('div', { class: 'bgb-tags', role: 'group', 'aria-label': t('ui.note') },
        list('notes').map((n) => h('button', {
            type: 'button', class: 'bgb-tag', text: n, 'aria-pressed': 'false',
            on: {
                click: (e) => {
                    const on = st.note !== n;
                    st.note = on ? n : '';
                    tags.querySelectorAll('.bgb-tag').forEach((b) => b.setAttribute('aria-pressed', String(b === e.currentTarget && on)));
                },
            },
        })));

    function paintSeg() {
        put(clear(seg), 
            ['pay', 'get'].map((d) => h('button', {
                type: 'button', role: 'radio', class: 'bgb-seg-b', 'aria-checked': String(st.dir === d),
                text: d === 'pay' ? '↑ ' + t('ui.dir_pay') : '↓ ' + t('ui.dir_get'),
                on: { click: () => { st.dir = d; if (st.cp === POT && d === 'pay' && !g.jackpot) st.cp = BANK; paintSeg(); paintCps(); paintPad(); paintSum(); } },
            })));
    }
    function paintCps() {
        const opts = [BANK];
        if (g.jackpot) opts.push(POT);
        for (const p of others) opts.push(p.id);
        if (others.length) opts.push('each');
        if (opts.indexOf(st.cp) < 0) st.cp = BANK;
        cpLabel.textContent = st.dir === 'pay' ? t('ui.to_whom') : t('ui.from_whom');
        put(clear(cps), opts.map((id) => {
            const p = L.player(g, id);
            let label;
            if (id === 'each') label = '👥 ' + (st.dir === 'pay' ? t('ui.to_each') : t('ui.from_each'));
            else if (id === POT) label = acctLabel(g, id) + ' · ' + money(rp.pot, g.cur);
            else label = acctLabel(g, id);
            return h('button', {
                type: 'button', role: 'radio', class: 'bgb-cp' + (p ? '' : ' bgb-cp-bank'), 'aria-checked': String(st.cp === id),
                style: p ? { '--pc': p.color } : null, text: label, 'data-cp': id,
                on: { click: () => { st.cp = id; paintCps(); paintPad(); paintSum(); } },
            });
        }));
    }
    function paintSum() {
        err.hidden = true;
        clear(sum);
        const a = st.amount;
        const each = st.cp === 'each';
        const potAll = st.cp === POT && st.dir === 'get' && !a;
        const amt = a ? money(a, g.cur) : (potAll ? money(rp.pot, g.cur) : money(0, g.cur));
        const from = st.dir === 'pay' ? acctChip(g, me.id) : (each ? h('span', { class: 'bgb-acct bgb-acct-bank', text: '👥 ' + t('ui.each_player', { n: others.length }) }) : acctChip(g, st.cp));
        const to = st.dir === 'pay' ? (each ? h('span', { class: 'bgb-acct bgb-acct-bank', text: '👥 ' + t('ui.each_player', { n: others.length }) }) : acctChip(g, st.cp)) : acctChip(g, me.id);
        put(sum, from, h('span', { class: 'bgb-arrow', 'aria-hidden': 'true', text: '→' }), to,
            h('b', { class: 'bgb-sum-amt', text: each && a ? t('ui.each_amount', { x: amt, n: others.length, total: money(a * others.length, g.cur) }) : amt }));
        const req = guest && st.dir === 'get';
        if (req) put(sum, h('span', { class: 'bgb-hint bgb-sum-note', text: st.cp === BANK || st.cp === POT ? (g.autoBank ? t('ui.req_auto') : t('ui.req_banker')) : t('ui.req_player') }));
        ok.textContent = req ? t('ui.btn_request') : (st.dir === 'pay' ? t('ui.btn_pay') : t('ui.btn_receive'));
        ok.disabled = !(a || potAll && rp.pot > 0);
    }

    async function submit() {
        if (ok.disabled) return;
        const a = st.amount;
        const note = st.note;
        let cmd;
        if (st.cp === 'each') cmd = { type: 'split', pid: me.id, dir: st.dir === 'pay' ? 'pay' : 'collect', amount: a, note };
        else if (guest && st.dir === 'get') cmd = { type: 'request', from: st.cp, amount: st.cp === POT && !a ? 0 : a, note };
        else if (st.dir === 'pay') cmd = { type: 'pay', from: me.id, to: st.cp, amount: a, note };
        else if (st.cp === POT && !a) cmd = { type: 'jackpot', pid: me.id };
        else cmd = { type: 'pay', from: st.cp, to: me.id, amount: a, note };
        ok.disabled = true;
        const res = await app.run(cmd);
        if (res && res.ok) {
            close();
            if (res.requested) app.say(t('ui.requested_n', { n: res.requested }), 'success');
            else if (res.request && !res.entry) app.say(t('ui.request_sent'), 'success');
            return;
        }
        ok.disabled = false;
        err.hidden = false;
        err.textContent = app.errText(res);
        sound('bad');
        if (res && res.err === 'funds' && !guest) {
            put(err, ' ', h('button', { type: 'button', class: 'bgb-link', text: t('ui.go_bankrupt'), on: { click: () => openBankrupt(app, res.pid || me.id) } }));
        }
    }
    ok.addEventListener('click', submit);

    paintSeg();
    paintCps();
    paintPad();
    paintSum();

    const tools = [];
    if (!guest) {
        if (g.salary > 0 && !rp.out[me.id]) tools.push(h('button', { type: 'button', class: 'bgb-chip', text: '＋ ' + t('ui.salary_btn', { x: money(g.salary, g.cur) }), on: { click: async () => { const r = await app.run({ type: 'salary', pid: me.id }); if (r.ok) close(); else app.say(app.errText(r), 'warning'); } } }));
        tools.push(h('button', { type: 'button', class: 'bgb-chip', text: '✎ ' + t('ui.edit_player'), on: { click: () => openEdit(app, me.id) } }));
        if (!rp.out[me.id]) tools.push(h('button', { type: 'button', class: 'bgb-chip bgb-chip-danger', text: '💥 ' + t('ui.bankrupt'), on: { click: () => openBankrupt(app, me.id) } }));
    }

    show(head,
        [seg, cpLabel, cps, padWrap, tags, tools.length ? h('div', { class: 'bgb-tools' }, tools) : null].filter(Boolean),
        [sum, err, h('div', { class: 'bgb-foot-row' }, h('button', { type: 'button', class: 'lg-btn lg-btn-ghost', text: t('ui.cancel'), on: { click: close } }), ok)]);
    onKey = (e) => {
        if (e.key === 'Enter' && !(e.target && e.target.tagName === 'BUTTON')) { submit(); return true; }
        return kp ? kp.key(e) : false;
    };
}

/* ── a player's card ──────────────────────────────────────────────────────── */

export function openEdit(app, pid) {
    const g = app.game;
    const p = L.player(g, pid);
    if (!p) return;
    const host = app.role === 'host';
    const rp = L.replay(g);
    const st = { name: p.name, token: p.token, color: p.color };
    const name = h('input', { type: 'text', class: 'bgb-in', id: 'bgbEditName', maxlength: '24', value: p.name, autocomplete: 'off' });
    name.addEventListener('input', () => { st.name = name.value; });
    const toks = h('div', { class: 'bgb-tokgrid', role: 'radiogroup', 'aria-label': t('ui.token_pick') });
    const cols = h('div', { class: 'bgb-swatches', role: 'radiogroup', 'aria-label': t('ui.color_pick') });
    function paint() {
        put(clear(toks), L.TOKENS.map((tk) => h('button', { type: 'button', role: 'radio', class: 'bgb-tokopt', text: tk, 'aria-checked': String(tk === st.token), on: { click: () => { st.token = tk; paint(); } } })));
        put(clear(cols), L.COLORS.map((c) => h('button', { type: 'button', role: 'radio', class: 'bgb-sw', style: { '--c': c }, 'aria-label': c, 'aria-checked': String(c === st.color), on: { click: () => { st.color = c; paint(); } } })));
    }
    paint();
    const body = [
        h('label', { class: 'bgb-lbl', for: 'bgbEditName', text: t('ui.name') }), name,
        h('div', { class: 'bgb-dlg-l', text: t('ui.token_pick') }), toks,
        h('div', { class: 'bgb-dlg-l', text: t('ui.color_pick') }), cols,
    ];
    if (host) {
        const acts = [];
        if (p.dev) acts.push(h('button', { type: 'button', class: 'bgb-chip', text: '📵 ' + t('ui.seat_free'), on: { click: async () => { const r = await app.run({ type: 'seat_free', pid }); if (r.ok) { app.say(t('ui.seat_freed', { name: p.name }), 'success'); close(); } } } }));
        if (rp.out[pid] && g.kind !== 'poker') acts.push(h('button', { type: 'button', class: 'bgb-chip', text: '↩ ' + t('ui.revive'), on: { click: async () => { const r = await app.run({ type: 'revive', pid }); if (r.ok) close(); else app.say(app.errText(r), 'warning'); } } }));
        if (g.kind === 'poker') {
            acts.push(h('button', { type: 'button', class: 'bgb-chip', text: '➕ ' + t('ui.rebuy', { x: num(g.start) }), on: { click: async () => { const r = await app.run({ type: 'rebuy', pid }); if (r.ok) close(); else app.say(app.errText(r), 'warning'); } } }));
            if (!rp.out[pid]) {
                acts.push(h('button', { type: 'button', class: 'bgb-chip', text: '🅳 ' + t('ui.make_dealer'), on: { click: async () => { const r = await app.run({ type: 'button', pid }); if (r.ok) close(); else app.say(app.errText(r), 'warning'); } } }));
                acts.push(h('button', { type: 'button', class: 'bgb-chip bgb-chip-danger', text: '🚪 ' + t('ui.cashout', { x: num(rp.bal[pid] || 0) }), on: { click: async () => { const r = await app.run({ type: 'cashout', pid }); if (r.ok) close(); else app.say(app.errText(r), 'warning'); } } }));
            }
        } else if (!rp.out[pid]) {
            acts.push(h('button', { type: 'button', class: 'bgb-chip bgb-chip-danger', text: '💥 ' + t('ui.bankrupt'), on: { click: () => openBankrupt(app, pid) } }));
        }
        if (p.dev || acts.length) body.push(h('div', { class: 'bgb-dlg-l', text: t('ui.player_actions') }), h('div', { class: 'bgb-tools' }, acts));
        if (p.dev) body.push(h('p', { class: 'bgb-hint', text: t('ui.seat_free_d') }));
    }
    const save = h('button', {
        type: 'button', class: 'lg-btn', text: t('ui.save'),
        on: {
            click: async () => {
                const r = await app.run({ type: 'player_edit', pid, name: st.name, token: st.token, color: st.color });
                if (r.ok) close(); else app.say(app.errText(r), 'warning');
            },
        },
    });
    show(h('div', { class: 'bgb-dlg-hd' }, h('span', { class: 'bgb-dlg-tok', style: { '--pc': p.color }, text: p.token, 'aria-hidden': 'true' }),
        h('div', { class: 'bgb-dlg-ti' }, h('div', { class: 'bgb-dlg-t', id: 'bgbDlgTitle', text: p.name }),
            h('div', { class: 'bgb-dlg-s', text: t('ui.balance') + ': ' + money(rp.bal[pid] || 0, g.cur) })), closeBtn()),
    body,
    h('div', { class: 'bgb-foot-row' }, h('button', { type: 'button', class: 'lg-btn lg-btn-ghost', text: t('ui.cancel'), on: { click: close } }), save));
    onKey = (e) => { if (e.key === 'Enter' && e.target === name) { save.click(); return true; } return false; };
}

/* ── bankruptcy ───────────────────────────────────────────────────────────── */

export function openBankrupt(app, pid) {
    const g = app.game;
    const p = L.player(g, pid);
    if (!p) return;
    const rp = L.replay(g);
    const bal = rp.bal[pid] || 0;
    let to = BANK;
    const opts = [BANK].concat(g.players.filter((q) => q.id !== pid && rp.joined[q.id] && !rp.out[q.id]).map((q) => q.id));
    const box = h('div', { class: 'bgb-cps', role: 'radiogroup', 'aria-label': t('ui.bankrupt_to') });
    function paint() {
        put(clear(box), opts.map((id) => h('button', {
            type: 'button', role: 'radio', class: 'bgb-cp' + (id === BANK ? ' bgb-cp-bank' : ''), 'aria-checked': String(to === id),
            style: id !== BANK ? { '--pc': L.player(g, id).color } : null, text: acctLabel(g, id),
            on: { click: () => { to = id; paint(); } },
        })));
    }
    paint();
    const go = h('button', {
        type: 'button', class: 'lg-btn bgb-danger', text: t('ui.bankrupt_go'),
        on: {
            click: async () => {
                const r = await app.run({ type: 'bankrupt', pid, to });
                if (r.ok) close(); else app.say(app.errText(r), 'warning');
            },
        },
    });
    show(h('div', { class: 'bgb-dlg-hd' }, h('span', { class: 'bgb-dlg-tok', style: { '--pc': p.color }, text: '💥', 'aria-hidden': 'true' }),
        h('div', { class: 'bgb-dlg-ti' }, h('div', { class: 'bgb-dlg-t', id: 'bgbDlgTitle', text: t('ui.bankrupt_title', { name: p.name }) })), closeBtn()),
    [h('p', { class: 'bgb-p', text: bal > 0 ? t('ui.bankrupt_d', { x: money(bal, g.cur) }) : (bal < 0 ? t('ui.bankrupt_debt', { x: money(-bal, g.cur) }) : t('ui.bankrupt_zero')) }),
        bal > 0 ? h('div', { class: 'bgb-dlg-l', text: t('ui.bankrupt_to') }) : null, bal > 0 ? box : null,
        h('p', { class: 'bgb-hint', text: t('ui.bankrupt_undo') })].filter(Boolean),
    h('div', { class: 'bgb-foot-row' }, h('button', { type: 'button', class: 'lg-btn lg-btn-ghost', text: t('ui.cancel'), on: { click: close } }), go));
}

/* ── add a player during the game ─────────────────────────────────────────── */

export function openAdd(app) {
    const g = app.game;
    const i = g.players.length;
    const used = new Set(g.players.map((p) => p.color));
    const usedT = new Set(g.players.map((p) => p.token));
    const st = {
        name: t('ui.player_n', { n: i + 1 }),
        color: L.COLORS.find((c) => !used.has(c)) || L.COLORS[i % L.COLORS.length],
        token: L.TOKENS.find((c) => !usedT.has(c)) || L.TOKENS[i % L.TOKENS.length],
    };
    const name = h('input', { type: 'text', class: 'bgb-in', id: 'bgbAddName', maxlength: '24', value: st.name, autocomplete: 'off' });
    name.addEventListener('input', () => { st.name = name.value; });
    const go = h('button', {
        type: 'button', class: 'lg-btn', text: t('ui.player_add'),
        on: {
            click: async () => {
                const r = await app.run({ type: 'player_add', name: st.name, color: st.color, token: st.token });
                if (r.ok) close(); else app.say(app.errText(r), 'warning');
            },
        },
    });
    show(h('div', { class: 'bgb-dlg-hd' }, h('div', { class: 'bgb-dlg-ti' }, h('div', { class: 'bgb-dlg-t', id: 'bgbDlgTitle', text: t('ui.player_add') })), closeBtn()),
        [h('label', { class: 'bgb-lbl', for: 'bgbAddName', text: t('ui.name') }), name,
            h('p', { class: 'bgb-hint', text: g.kind === 'poker' ? t('ui.add_d_poker', { x: num(g.start) }) : t('ui.add_d', { x: money(g.start, g.cur) }) })],
        h('div', { class: 'bgb-foot-row' }, h('button', { type: 'button', class: 'lg-btn lg-btn-ghost', text: t('ui.cancel'), on: { click: close } }), go));
    onKey = (e) => { if (e.key === 'Enter') { go.click(); return true; } return false; };
}

/* ── yes / no ─────────────────────────────────────────────────────────────── */

export function confirm(title, text, okLabel, danger) {
    return new Promise((resolve) => {
        const yes = h('button', { type: 'button', class: 'lg-btn' + (danger ? ' bgb-danger' : ''), text: okLabel || t('ui.ok') });
        const no = h('button', { type: 'button', class: 'lg-btn lg-btn-ghost', text: t('ui.cancel') });
        show(h('div', { class: 'bgb-dlg-hd' }, h('div', { class: 'bgb-dlg-ti' }, h('div', { class: 'bgb-dlg-t', id: 'bgbDlgTitle', text: title })), closeBtn()),
            [h('p', { class: 'bgb-p', text })],
            h('div', { class: 'bgb-foot-row' }, no, yes), 'bgb-dlg-sm');
        resolveConfirm = resolve;
        yes.addEventListener('click', () => { const r = resolveConfirm; resolveConfirm = null; closeDialog(dlg()); if (r) r(true); });
        no.addEventListener('click', () => close());
    });
}

/* ── raise amount (poker) ─────────────────────────────────────────────────── */

/** o = options(hand, pid); resolves with the «raise to» amount or null. */
export function askRaise(app, pid, o, pot) {
    return new Promise((resolve) => {
        const g = app.game;
        const p = L.player(g, pid);
        const unit = (g.poker && g.poker.unit) || 1;
        let v = o.minTo;
        const round = (x) => Math.max(o.minTo, Math.min(o.maxTo, Math.round(x / unit) * unit));
        const out = h('output', { class: 'bgb-amt' });
        const range = h('input', { type: 'range', class: 'bgb-range', min: String(o.minTo), max: String(o.maxTo), step: String(unit), value: String(v), 'aria-label': t('pk.raise_to') });
        const hint = h('div', { class: 'bgb-hint' });
        const go = h('button', { type: 'button', class: 'lg-btn' });
        function paint() {
            out.textContent = num(v);
            range.value = String(v);
            go.textContent = (v === o.maxTo ? t('pk.allin') + ' ' : (o.bet ? t('pk.bet') + ' ' : t('pk.raise_to') + ' ')) + num(v);
            hint.textContent = v < o.maxTo && v < o.minTo ? '' : t('pk.raise_hint', { min: num(o.minTo), max: num(o.maxTo) });
        }
        range.addEventListener('input', () => { v = round(+range.value); paint(); });
        const kp = keypad({ value: null, quick: [], onChange: (n) => { if (n) { v = Math.max(o.minTo, Math.min(o.maxTo, n)); out.textContent = num(n); range.value = String(v); go.textContent = t('pk.raise_to') + ' ' + num(v); } } });
        kp.el.querySelector('.bgb-quick').hidden = true;
        kp.el.querySelector('.bgb-amt').hidden = true;
        const presets = h('div', { class: 'bgb-quick' },
            [[t('pk.min'), o.minTo], [t('pk.half_pot'), o.cur + (pot + o.callAmt) / 2], [t('pk.pot'), o.cur + pot + o.callAmt], [t('pk.allin'), o.maxTo]]
                .map(([lbl, x]) => h('button', { type: 'button', class: 'bgb-chip', text: lbl + ' · ' + num(round(x)), on: { click: () => { v = round(x); paint(); } } })));
        show(h('div', { class: 'bgb-dlg-hd' }, h('span', { class: 'bgb-dlg-tok', style: { '--pc': p.color }, text: p.token, 'aria-hidden': 'true' }),
            h('div', { class: 'bgb-dlg-ti' }, h('div', { class: 'bgb-dlg-t', id: 'bgbDlgTitle', text: p.name }), h('div', { class: 'bgb-dlg-s', text: (o.bet ? t('pk.bet') : t('pk.raise_to')) })), closeBtn()),
        [out, range, presets, kp.el, hint],
        h('div', { class: 'bgb-foot-row' }, h('button', { type: 'button', class: 'lg-btn lg-btn-ghost', text: t('ui.cancel'), on: { click: () => { close(); resolve(null); } } }), go));
        resolveConfirm = (x) => resolve(x ? v : null);
        go.addEventListener('click', () => { resolveConfirm = null; closeDialog(dlg()); resolve(v); });
        onKey = (e) => {
            if (e.key === 'Enter') { go.click(); return true; }
            return kp.key(e);
        };
        paint();
    });
}

